import { storeRegionDetection, setDectectedLabels } from "../context/utils.js";

// Calculate the absolute distance between two points along the given axis (X or Y)
const calculateDistance = (p1, p2, axis) => {
  axis = axis.toUpperCase();
  if (axis === "X") {
    return Math.abs(p2[0] - p1[0]);
  } else if (axis === "Y") {
    return Math.abs(p2[1] - p1[1]);
  } else {
    throw new Error("Axis must be 'X' or 'Y'");
  }
};

// Check if point 'b' lies strictly between points 'a' and 'c' along the specified axis
const isBetween = (a, b, c, axis) => {
  axis = axis.toUpperCase();
  if (axis === "X") {
    return Math.min(a[0], c[0]) < b[0] && b[0] < Math.max(a[0], c[0]);
  } else if (axis === "Y") {
    return Math.min(a[1], c[1]) < b[1] && b[1] < Math.max(a[1], c[1]);
  } else {
    throw new Error("Axis must be 'X' or 'Y'");
  }
};

// Check if the mask points lie between stencil points and calculate distances
const calculateDistanceAndCheckPoint = (stencil1, stencil2, mask1, mask2, axis, region = null) => {
  const p1Between = isBetween(stencil1, mask1, stencil2, axis);
  const p2Between = isBetween(stencil1, mask2, stencil2, axis);

  const stencilDistance = calculateDistance(stencil1, stencil2, axis);
  const maskDistance = calculateDistance(mask1, mask2, axis);

  return [p1Between, p2Between, stencilDistance, maskDistance];
};

// Get the min and max points from multiple bounding boxes after scaling
const minMaxOfMask = (detections) => {
  let xmin = 1, ymin = 1;
  let xmax = 0, ymax = 0;

  for (const item of detections) {
    const x1 = item.bbox[0];
    const y1 = item.bbox[1];
    const x2 = x1 + item.bbox[2];
    const y2 = y1 + item.bbox[3];

    if (x1 < xmin) xmin = x1;
    if (y1 < ymin) ymin = y1;
    if (x2 > xmax) xmax = x2;
    if (y2 > ymax) ymax = y2;
  }

  return [
    [xmin * 128 - 7, ymin * 128 - 7],
    [xmax * 128 + 7, ymax * 128 + 7],
  ];
};

// Main function to determine if the frame is good based on detections and region
export const goodFrame = (detections, currentRegion) => {
  let xData = []
  let yData = []
  // Return 3 if no detections are found
  if (!detections || detections.length === 0) {
    return [3, [], xData, yData, currentRegion];
  }

  const stencilMin = [0, 0];
  const stencilMax = [128, 128];

  const [maskMin, maskMax] = minMaxOfMask(detections);

  const [p1BetweenX, p2BetweenX, stencilDistanceX, maskDistanceX] =
    calculateDistanceAndCheckPoint(stencilMin, stencilMax, maskMin, maskMax, "X");
  xData = [p1BetweenX, p2BetweenX, stencilDistanceX, maskDistanceX]

  const [p1BetweenY, p2BetweenY, stencilDistanceY, maskDistanceY] =
    calculateDistanceAndCheckPoint(stencilMin, stencilMax, maskMin, maskMax, "Y");
  yData = [p1BetweenY, p2BetweenY, stencilDistanceY, maskDistanceY]

  if (currentRegion == "Side-Right" || currentRegion == "Side-Left") {
    if (p1BetweenX && p2BetweenX && p1BetweenY && p2BetweenY) {
      if (
        0.75 * stencilDistanceX < maskDistanceX &&
        maskDistanceX <= stencilDistanceX*0.97 &&
        0.6 * stencilDistanceY <= maskDistanceY &&
        maskDistanceY < stencilDistanceY*0.97
      ) {
        return [1, [maskMin, maskMax], xData, yData, currentRegion]; // Good fit inside the stencil region
      } else {
        return [2, [maskMin, maskMax], xData, yData, currentRegion]; // Inside stencil but size is not within threshold (zoomed out)
      }
    } else if (!p1BetweenX || !p2BetweenX) {
      return [0, [maskMin, maskMax], xData, yData, currentRegion]; // Mask is outside stencil area on X-axis (zoomed in or vehicle not fit properly in the frame)
    }
  } else {
    if (p1BetweenX && p2BetweenX && p1BetweenY && p2BetweenY) {
      if (
        0.7 * stencilDistanceY <= maskDistanceY &&
        maskDistanceY <= stencilDistanceY*0.97 &&
        0.50 * stencilDistanceX <= maskDistanceX &&
        maskDistanceX <= stencilDistanceX*0.97
      ) {
        return [1, [maskMin, maskMax], xData, yData, currentRegion]; // Good fit for Front or Rear region
      } else {
        return [2, [maskMin, maskMax], xData, yData, currentRegion]; // Inside stencil but size is off (zoomed out)
      }
    } else if (!p1BetweenY || !p2BetweenY) {
      return [0, [maskMin, maskMax], xData, yData, currentRegion]; // Mask is outside stencil area on Y-axis (zoomed in or vehicle not fit properly in the frame)
    }
  }

  return [4, [maskMin, maskMax], xData, yData, currentRegion]; // Fallback value if none of the conditions match
};

// Determine the vehicle region based on the input angle
function checkRegionFromAngle(angle, region) {
  if (region === "Front" && (angle >= 335 || angle <= 25)) {
    return true;
  } else if (region === "Front-Isometric-Right" && angle >= 15 && angle <= 80) {
    return true;
  } else if (region === "Side-Right" && angle >= 40 && angle <= 140) {
    return true;
  } else if (region === "Rear-Isometric-Right" && angle >= 100 && angle <= 170) {
    return true;
  } else if (region === "Rear" && angle >= 150 && angle <= 210) {
    return true;
  } else if (region === "Rear-Isometric-Left" && angle >= 190 && angle <= 260) {
    return true;
  } else if (region === "Side-Left" && angle >= 220 && angle <= 320) {
    return true;
  } else if (region === "Front-Isometric-Left" && angle >= 280 && angle <= 345) {
    return true;
  } else {
    return false;
  }
}


/**
 * Verifies if the detected parts correspond to the expected region and are sufficient.
 * 
 * @param {string} currentRegion - Region detected by external logic (e.g., prediction or metadata)
 * @param {Array<Object>} detections - List of detected objects, each with a 'label' key
 * @param {number} angle - Viewing angle of the vehicle (0 to 360 degrees)
 * @returns {Object} - An object containing:
 *    - region_flag: whether the angle-based region matches the current region
 *    - complete_part_flag: whether all expected parts were detected
 *    - undetected_part_list: parts that were expected but not detected
 */
export function regionAndPartsVerification(currentRegion, detections, angle) {
    // Mapping of regions to the parts expected in those regions
    const regionPartMapping = {
        "Front": ["front_bumper", "hood", "front_glass"],
        "Rear": ["back_bumper", "dicky", "back_glass"],
        "Side-Left": ["front_door", "back_door", "window_glass", "running_board"],
        "Side-Right": ["front_door", "back_door", "window_glass", "running_board"],
        "Front-Isometric-Left": ["fender"],
        "Front-Isometric-Right": ["fender"],
        "Rear-Isometric-Right": ["qtr_panel"],
        "Rear-Isometric-Left": ["qtr_panel"]
    };

    // Extract only the valid detected part labels into a Set for quick lookup
    const detectedLabels = new Set(
        detections
            .map(d => d.label)
            .filter(label => !!label)  // remove null/undefined/empty labels
    );

    setDectectedLabels(detectedLabels);

    storeRegionDetection(currentRegion, Array.from(detectedLabels), angle);

    // Determine the region based on the input angle
    const checkRegion = checkRegionFromAngle(angle, currentRegion);

    // Check if the angle-based region matches the provided current region
    if (!checkRegion) {
        return {
            region_flag: false,
            complete_part_flag: false,
            undetected_part_list: []
        };
    }

    // Get expected parts for the detected region
    const expectedParts = new Set(regionPartMapping[currentRegion] || []);

    // Find matched parts between detected and expected
    const matchedParts = [...expectedParts].filter(part => detectedLabels.has(part));

    // For major regions, at least 2 matches are required; for others, 1 is enough
    const minRequired = ["Front", "Rear", "Side-Left", "Side-Right"].includes(currentRegion) ? 2 : 1;

    // If not enough parts were detected, return failure
    if (matchedParts.length < minRequired) {
        return {
            region_flag: false,
            complete_part_flag: false,
            undetected_part_list: []
        };
    }

    // Find which expected parts were not detected
    const undetectedParts = [...expectedParts].filter(part => !detectedLabels.has(part));

    // Final result including whether all expected parts were found
    return {
        region_flag: true,
        complete_part_flag: undetectedParts.length === 0,
        undetected_part_list: undetectedParts
    };
}
